import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerForsikring() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Trailerforsikring", path: "/trailer-forsikring" }
  ];
  const tocItems = [
    { id: "lovpligtig-ansvar", title: "Er traileren dækket af bilens ansvarsforsikring?" },
    { id: "kaskoforsikring", title: "Dækker bilens kasko skader på selve traileren?" },
    { id: "tyveri-af-trailer", title: "Hvad gælder ved tyveri af trailer og gods?" },
    { id: "krav-til-laas", title: "Krav om godkendt trailerlås" },
    { id: "hvad-koster-trailerforsikring", title: "Hvad koster en kaskoforsikring til trailer?" }
  ];

  const faq = [
    {
      q: "Skal man have en lovpligtig ansvarsforsikring på en trailer?",
      a: "Nej. En trailer har ikke sin egen ansvarsforsikring. Ifølge færdselslovens § 105 er skader, som traileren forvolder på andre personer eller andres ejendom, automatisk dækket af den trækkende bils lovpligtige ansvarsforsikring, så længe traileren er tilkoblet eller løsriver sig under kørsel."
    },
    {
      q: "Dækker bilens kaskoforsikring, hvis traileren bliver beskadiget?",
      a: "Nej, trækbilens kaskoforsikring dækker kun skader på selve bilen. Skader på selve traileren (f.eks. hvis den vælter, påkøres, eller hvis du bakker ind i en mur) er kun dækket, hvis du har tegnet en separat kaskoforsikring på traileren."
    },
    {
      q: "Dækker min indboforsikring, hvis traileren bliver stjålet fra min indkørsel?",
      a: "Normalt nej. Indregistreringspligtige motorkøretøjer og påhængsvogne er som hovedregel undtaget fra almindelige indboforsikringer. For at få erstatning ved tyveri af en indregistreret trailer skal du have en særskilt kaskoforsikring."
    },
    {
      q: "Er godset og bagagen i traileren forsikret?",
      a: "Gods og inventar på traileren er som udgangspunkt ikke dækket af trailerens kaskoforsikring. Privat gods kan i visse tilfælde være dækket af din indboforsikring ved pludselig skade eller færdselsuheld, men der gælder ofte særlige undtagelser for transport på åbne ladflader."
    }
  ];

  const sources = [
    { title: "Færdselslovens § 101 og § 105 om motorkøretøjers erstatningsansvar (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" },
    { title: "Bekendtgørelse om ansvarsforsikring for motordrevne køretøjer m.v. (BEK nr. 1561 af 18/12/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1561" }
  ];

  return (
    <>
      <SEO 
        title="Trailer forsikring: Ansvar, kasko og tyveri forklaret" 
        description="Er traileren forsikret gennem bilen? Få klarhed over færdselslovens ansvarsregler, hvorfor bilens kasko ikke dækker traileren og krav til lås ved tyveri." 
        pathname="/trailer-forsikring"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailerforsikring: Ansvar, kasko og tyveri
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Når en trailer er tilkoblet en bil, er eventuelle skader på andre trafikanter og deres ejendom <strong>automatisk dækket af trækbilens lovpligtige ansvarsforsikring</strong>. Der skal altså ikke tegnes en særskilt ansvarsforsikring på en trailer.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Til gengæld dækker bilens forsikring <em>aldrig skader på selve traileren</em> eller tyveri af traileren, når den står parkeret. Ønsker du økonomisk dækning ved buler, trafikuheld eller tyveri, kræver det en separat kaskoforsikring på traileren.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Det vigtigste skel i forsikringen">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Skade på <strong>andre</strong> (ansvar): Dækkes af <strong>bilens ansvarsforsikring</strong>.</li>
            <li>• Skade på <strong>egen trailer</strong> (kasko): Kræver <strong>separat trailerkasko</strong>.</li>
            <li>• <strong>Tyveri</strong> af trailer: Kræver <strong>separat trailerkasko</strong> + godkendt lås.</li>
          </ul>
        </Callout>

        <h2 id="lovpligtig-ansvar" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Er traileren dækket af bilens ansvarsforsikring?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I henhold til færdselslovens § 105 hæfter trækbilens motoransvarsforsikring for skader forvoldt af vogntoget som en samlet enhed.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Det betyder i praksis:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Hvis du under bakning rammer en anden bil med trailerens hjørne, dækker bilens ansvarsforsikring modpartens skade.</li>
          <li>Hvis traileren river sig løs fra anhængertrækket under kørslen og rammer en fodgænger eller modkørende bil, er det ligeledes bilens ansvarsforsikring, der erstatter skaderne.</li>
          <li>Modpartens personskader og tingskader erstattes altid efter færdselslovens objektive erstatningsregler.</li>
        </ul>

        <h2 id="kaskoforsikring" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Dækker bilens kasko skader på selve traileren?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Et af de mest udbredte fejlantagelser blandt trailer-ejere er, at bilens kaskoforsikring også omfatter det påhængte køretøj. Det gør den <strong>ikke</strong>.
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <p className="text-[16px] text-[#1A1C1F] m-0">
            Hvis du mister herredømmet i et glatføreuheld eller overskrider tilladt hastighed, så traileren skrider ud og vælter i grøften, dækker bilens kaskoforsikring alene reparationen af bilens bagende og kofanger.
          </p>
          <p className="text-[15px] text-[#5C636B] m-0">
            Hvis trailerens aksel er bøjet, eller kassen er knust, får du 0 kr. i erstatning for traileren, medmindre du særskilt har tegnet en trailerkaskoforsikring via dit forsikringsselskab.
          </p>
        </div>

        <h2 id="tyveri-af-trailer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad gælder ved tyveri af trailer og gods?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Trailere er et eftertragtet mål for tyveknægte, da de ofte står let tilgængelige i indkørsler eller ved byggepladser.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          En almindelig familieforsikring (indboforsikring) dækker generelt ikke registreringspligtige motorkøretøjer og påhængsvogne. Hvis en uindregistreret havevogn (f.eks. til en plænetraktor) stjæles fra en aflåst garage, kan den i visse tilfælde erstattes af indboforsikringen som haveudstyr, men <strong>indregistrerede trailere med nummerplade er altid undtaget</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          For at få erstattet en stjålet trailer skal der foreligge en aktiv kaskoforsikring på trailerens police.
        </p>

        <h2 id="krav-til-laas" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Krav om godkendt trailerlås
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Forsikringsselskaber stiller næsten undtagelsesfrit krav om, at en parkeret trailer skal være aflåst med en <strong>forsikringsgodkendt lås</strong> (godkendt af Forsikring &amp; Pension / F&amp;P):
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Kuglelås / boks:</strong> En kraftig stålkasse, der omslutter koblingshovedet og forhindrer, at traileren kan kobles på en fremmed bil.</li>
          <li><strong>Hjullås (hjulklemme):</strong> En kraftig klemme omkring det ene hjul, der blokerer hjulet mod at rotere.</li>
          <li>En simpel hængelås gennem koblingsgrebet godkendes sjældent som tilstrækkelig sikring af forsikringsselskaberne ved uovervåget parkering.</li>
        </ul>

        <h2 id="hvad-koster-trailerforsikring" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad koster en kaskoforsikring til trailer?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Prisen på en trailerkasko afhænger af trailerens nyværdi, totalvægt og den valgte selvrisiko:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Trailertype</th>
                <th className="py-2.5 pr-4 font-semibold">Værdi</th>
                <th className="py-2.5 pr-4 font-semibold">Typisk årlig præmie</th>
                <th className="py-2.5 font-semibold">Hvad dækkes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Lille havetrailer (ubremset)</td>
                <td className="py-3 pr-4">Under 10.000 kr.</td>
                <td className="py-3 pr-4">250 – 450 kr./år</td>
                <td className="py-3 text-[#5C636B]">Kasko og tyveri (ofte fravælges kasko pga. lav værdi)</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Almindelig boxtrailer / erhvervstrailer</td>
                <td className="py-3 pr-4">15.000 – 40.000 kr.</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">450 – 800 kr./år</td>
                <td className="py-3 text-[#5C636B]">Fuld kasko, brand, væltning og tyveri</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Hestetrailer / bådtrailer</td>
                <td className="py-3 pr-4">Over 50.000 kr.</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">800 – 1.600 kr./år</td>
                <td className="py-3 text-[#5C636B]">Omfattende dækning inkl. redning og specialudstyr</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs også om afgifter i vores artikel om <Link to="/trailer-vaegtafgift" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer vægtafgift</Link> og reglerne for syn i <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
